import React, { useState } from 'react';
import { saveProfile } from '../utils/storage';
import { Scale, Sparkles, Zap, Sprout, Brain, Shield } from 'lucide-react';

export default function Onboarding({ onComplete }) {
    const [step, setStep] = useState(0);
    const [profile, setProfile] = useState({
        goal: 'balanced',
        sexAtBirth: '',
        ageBand: '',
        pregnant: false,
        breastfeeding: false,
        pcos: false,
        thyroidCondition: false,
        hormonalContraception: false,
        acneProne: false,
        ttc: false,
    });

    const GOAL_OPTIONS = [
        { id: 'balanced', label: 'Balance my hormones', Icon: Scale, desc: 'All axes tracked equally', color: '#6366f1' },
        { id: 'skin', label: 'Reduce acne / improve skin', Icon: Sparkles, desc: 'Prioritizes skin & microbiome', color: '#a78bfa' },
        { id: 'metabolic', label: 'Improve energy & metabolism', Icon: Zap, desc: 'Flags insulin & UPF signals', color: '#f59e0b' },
        { id: 'fertility', label: 'Support fertility / TTC', Icon: Sprout, desc: 'Flags reproductive EDCs heavier', color: '#10b981' },
        { id: 'stress', label: 'Manage stress & cortisol', Icon: Brain, desc: 'Prioritizes stress-axis factors', color: '#8b5cf6' },
        { id: 'thyroid', label: 'Support thyroid health', Icon: Shield, desc: 'Flags PFAS & flame retardants', color: '#06b6d4' },
    ];

    const handleSave = () => {
        saveProfile(profile);
        onComplete();
    };

    const steps = [
        // Step 0: Goal Selection
        <div className="onboarding-step" key="welcome">
            <h1 className="onboarding-title" style={{ fontSize: 28, marginTop: 16 }}>What's your main goal?</h1>
            <p className="onboarding-desc" style={{ marginBottom: 24 }}>
                We'll tune your signals to what matters most for you.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', textAlign: 'left' }}>
                {GOAL_OPTIONS.map(g => (
                    <div
                        key={g.id}
                        onClick={() => setProfile(p => ({ ...p, goal: g.id }))}
                        style={{
                            display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 14,
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-lg)',
                            border: profile.goal === g.id ? '2px solid #6366f1' : '1px solid var(--border-medium)',
                            background: profile.goal === g.id ? 'rgba(99,102,241,0.08)' : 'var(--bg-secondary)',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${g.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <g.Icon size={20} color={g.color} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>{g.label}</div>
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{g.desc}</div>
                        </div>
                    </div>
                ))}
            </div>
            <button
                className="btn btn-primary btn-block"
                onClick={() => setStep(1)}
                disabled={!profile.goal}
                style={{ marginTop: 24, opacity: !profile.goal ? 0.5 : 1 }}
            >
                Continue
            </button>
        </div>,

        // Step 1: Basic info
        <div className="onboarding-step" key="basic">
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>About You</h2>
            <p className="page-subtitle" style={{ marginBottom: 24 }}>
                This helps us personalize your scoring. All data stays on your device.
            </p>
            <div className="onboarding-form">
                <div className="input-group">
                    <label className="input-label">Sex at birth</label>
                    <div className="chip-group">
                        {['Female', 'Male', 'Intersex'].map(s => (
                            <div
                                key={s}
                                className={`chip ${profile.sexAtBirth === s.toLowerCase() ? 'active' : ''}`}
                                onClick={() => setProfile(p => ({ ...p, sexAtBirth: s.toLowerCase() }))}
                            >
                                {s}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="input-group">
                    <label className="input-label">Age range</label>
                    <div className="chip-group">
                        {['18-25', '26-35', '36-45', '46-55', '55+'].map(a => (
                            <div
                                key={a}
                                className={`chip ${profile.ageBand === a ? 'active' : ''}`}
                                onClick={() => setProfile(p => ({ ...p, ageBand: a }))}
                            >
                                {a}
                            </div>
                        ))}
                    </div>
                </div>
                <button
                    className="btn btn-primary btn-block"
                    onClick={() => setStep(2)}
                    disabled={!profile.sexAtBirth || !profile.ageBand}
                    style={{ marginTop: 16, opacity: (!profile.sexAtBirth || !profile.ageBand) ? 0.4 : 1 }}
                >
                    Continue
                </button>
            </div>
        </div>,

        // Step 2: Health flags
        <div className="onboarding-step" key="flags">
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Health Context</h2>
            <p className="page-subtitle" style={{ marginBottom: 24 }}>
                Optional — these adjust scoring sensitivity. Toggle any that apply.
            </p>
            <div className="onboarding-form">
                <div className="card">
                    {[
                        { key: 'acneProne', label: 'Acne-prone / sensitive skin' },
                        { key: 'ttc', label: 'Trying to conceive (TTC)' },
                        { key: 'pregnant', label: 'Pregnant' },
                        { key: 'breastfeeding', label: 'Breastfeeding' },
                        { key: 'pcos', label: 'PCOS' },
                        { key: 'thyroidCondition', label: 'Thyroid condition' },
                        { key: 'hormonalContraception', label: 'Hormonal contraception', note: 'This helps interpret cycle-suppressed reproductive signals.' },
                    ].map(({ key, label, note }) => (
                        <div className="toggle-row" key={key} style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '16px' }}>
                            <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="toggle-label" style={{ fontSize: 15 }}>{label}</span>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={profile[key]}
                                        onChange={(e) => setProfile(p => ({ ...p, [key]: e.target.checked }))}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>
                            {note && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.4, paddingRight: 40 }}>{note}</div>}
                        </div>
                    ))}
                </div>
                <button
                    className="btn btn-primary btn-block"
                    onClick={handleSave}
                    style={{ marginTop: 24 }}
                >
                    Start Tracking →
                </button>
            </div>
        </div>,
    ];

    return (
        <div className="onboarding">
            <div className="step-indicator">
                {[0, 1, 2].map(i => (
                    <div key={i} className={`step-dot ${step === i ? 'active' : ''}`} />
                ))}
            </div>
            {steps[step]}
        </div>
    );
}
